/**
 * PlatformTenantsTab — iter67
 *
 * Cross-tenant management UI for `platform_admin`-role site admins.
 * Gated at the SiteAdmin parent (tab is only rendered for that role)
 * AND at the backend (every endpoint depends on get_current_platform_admin).
 *
 * UX:
 *   - Table of tenants (default tenant pinned first)
 *   - Inline "Mobile" toggle (instant PATCH, optimistic)
 *   - "Edit" button opens a small inline editor for:
 *       custom_domain, lifecycle_state, name
 *   - "+ Create tenant" button opens a create dialog
 *   - Default tenant: mobile/domain editable, lifecycle locked (UI tooltip)
 *
 * Failure surface: each operation surfaces backend errors via sonner toast.
 * No optimistic locking — the UI just refetches after a successful write.
 */
import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    Globe, Plus, Edit2, X, Smartphone, AlertTriangle, CheckCircle2, Shield, Loader2, Search, Trash2,
} from "lucide-react";
import TenantProvisioningWizard from "./TenantProvisioningWizard";

const LIFECYCLE_OPTIONS = [
    { value: "active", label: "Active", className: "text-emerald-400" },
    { value: "suspended", label: "Suspended", className: "text-amber-400" },
    { value: "archived", label: "Archived", className: "text-zinc-500" },
];

function LifecycleBadge({ state }) {
    const opt = LIFECYCLE_OPTIONS.find((o) => o.value === state) || LIFECYCLE_OPTIONS[0];
    return (
        <span
            data-testid={`tenant-lifecycle-badge-${state}`}
            className={`pill text-[10px] font-bold uppercase ${opt.className} border-current`}
        >
            {opt.label}
        </span>
    );
}

export default function PlatformTenantsTab() {
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState(null);
    const [createOpen, setCreateOpen] = useState(false);
    // iter84.64 — search / filter / bulk lifecycle actions
    const [query, setQuery] = useState("");
    const [stateFilter, setStateFilter] = useState("all");
    const [selected, setSelected] = useState(new Set());
    const [bulkAction, setBulkAction] = useState(null); // 'active'|'suspended'|'archived'|'delete'
    const [bulkBusy, setBulkBusy] = useState(false);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.listTenants();
            setTenants(r.tenants || []);
            setSelected(new Set());
        } catch (err) {
            if (err.response?.status === 403) {
                toast.error("Platform admin role required to view tenants");
            } else {
                toast.error(err.response?.data?.detail || "Failed to load tenants");
            }
        }
        setLoading(false);
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    const toggleMobile = async (t) => {
        try {
            const r = await api.updateTenant(t.id, { mobile_enabled: !t.mobile_enabled });
            setTenants((prev) => prev.map((x) => (x.id === t.id ? r.tenant : x)));
            toast.success(`Mobile ${r.tenant.mobile_enabled ? "enabled" : "disabled"} for ${t.name}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to toggle mobile");
        }
    };

    const q = query.trim().toLowerCase();
    const visible = tenants.filter((t) => {
        if (stateFilter !== "all" && t.lifecycle_state !== stateFilter) return false;
        if (!q) return true;
        return [t.name, t.slug, t.custom_domain, t.id]
            .some((v) => (v || "").toLowerCase().includes(q));
    });

    const toggleSelect = (id) => setSelected((prev) => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });
    const eligible = visible.filter((t) => !t.is_default);
    const allSelected = eligible.length > 0 && eligible.every((t) => selected.has(t.id));
    const toggleSelectAll = () => setSelected(
        allSelected ? new Set() : new Set(eligible.map((t) => t.id))
    );

    const runBulk = async () => {
        const targets = tenants.filter((t) => selected.has(t.id) && !t.is_default);
        setBulkBusy(true);
        let ok = 0;
        const errors = [];
        for (const t of targets) {
            try {
                if (bulkAction === "delete") await api.deleteTenant(t.id);
                else await api.updateTenant(t.id, { lifecycle_state: bulkAction });
                ok += 1;
            } catch (err) {
                errors.push(`${t.name}: ${err.response?.data?.detail || "failed"}`);
            }
        }
        setBulkBusy(false);
        setBulkAction(null);
        if (ok) toast.success(`${ok} tenant(s) ${bulkAction === "delete" ? "deleted" : `set to ${bulkAction}`}`);
        errors.forEach((e) => toast.error(e));
        refresh();
    };

    return (
        <div className="space-y-4" data-testid="platform-tenants-tab">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h2 className="font-display font-extrabold text-xl text-white flex items-center gap-2">
                        <Globe className="w-5 h-5 text-cyber" /> Tenants
                    </h2>
                    <p className="text-xs text-zinc-500 mt-1 max-w-2xl leading-relaxed">
                        Cross-tenant operations. Toggle mobile access, assign custom domains, and manage tenant lifecycle. <span className="text-amber-400">Commons Admin only.</span>
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => setCreateOpen(true)}
                    data-testid="create-tenant-btn"
                    className="brutal-btn-primary text-xs py-2"
                >
                    <Plus className="w-3.5 h-3.5" /> Create tenant
                </button>
            </div>

            <div className="flex items-center gap-2 flex-wrap" data-testid="tenants-toolbar">
                <div className="relative flex-1 min-w-[220px] max-w-md">
                    <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-600" />
                    <input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search name, slug, domain…"
                        data-testid="tenants-search-input"
                        className="brutal-input w-full text-sm pl-8"
                    />
                </div>
                <select
                    value={stateFilter}
                    onChange={(e) => setStateFilter(e.target.value)}
                    data-testid="tenants-state-filter"
                    className="brutal-input text-sm py-1.5"
                >
                    <option value="all">All states</option>
                    {LIFECYCLE_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                </select>
                <span className="text-[10px] text-zinc-600" data-testid="tenants-count">
                    {visible.length} / {tenants.length} shown
                </span>
            </div>

            {selected.size > 0 && (
                <div className="brutal-card p-3 flex items-center gap-2 flex-wrap border-cyber/40" data-testid="tenants-bulk-bar">
                    <span className="text-xs font-bold text-white">{selected.size} selected</span>
                    <button type="button" onClick={() => setBulkAction("active")} data-testid="bulk-activate-btn" className="brutal-btn text-[10px] py-1 px-2 text-emerald-300 border-emerald-800">Activate</button>
                    <button type="button" onClick={() => setBulkAction("suspended")} data-testid="bulk-suspend-btn" className="brutal-btn text-[10px] py-1 px-2 text-amber-300 border-amber-800">Suspend</button>
                    <button type="button" onClick={() => setBulkAction("archived")} data-testid="bulk-archive-btn" className="brutal-btn text-[10px] py-1 px-2 text-zinc-300">Archive</button>
                    <button type="button" onClick={() => setBulkAction("delete")} data-testid="bulk-delete-btn" className="brutal-btn text-[10px] py-1 px-2 text-rose-300 border-rose-800"><Trash2 className="w-3 h-3 inline mr-1" />Delete</button>
                    <button type="button" onClick={() => setSelected(new Set())} className="ml-auto brutal-btn-ghost text-[10px] py-1 px-2">Clear</button>
                </div>
            )}

            {bulkAction && (
                <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="tenants-bulk-modal">
                    <div className="brutal-card p-5 max-w-md w-full space-y-3 border-2 border-[#333] bg-[#0d0d0d]">
                        <h3 className="font-display font-extrabold text-sm uppercase text-white">
                            {bulkAction === "delete" ? "Delete tenants?" : `Set ${selected.size} tenant(s) to ${bulkAction}?`}
                        </h3>
                        <ul className="text-xs text-zinc-400 max-h-40 overflow-auto space-y-0.5">
                            {tenants.filter((t) => selected.has(t.id) && !t.is_default).map((t) => (
                                <li key={t.id}>• {t.name} <span className="text-zinc-600 font-mono">({t.slug})</span></li>
                            ))}
                        </ul>
                        {bulkAction === "delete" && (
                            <p className="text-[10px] text-rose-300">
                                Delete is permanent and only allowed for archived tenants with zero
                                leagues — anything else is skipped with an error. The default tenant
                                is always excluded.
                            </p>
                        )}
                        <div className="flex justify-end gap-2">
                            <button type="button" onClick={() => setBulkAction(null)} data-testid="bulk-modal-cancel" className="brutal-btn-ghost text-xs py-1.5">Cancel</button>
                            <button
                                type="button"
                                onClick={runBulk}
                                disabled={bulkBusy}
                                data-testid="bulk-modal-confirm"
                                className={`text-xs py-1.5 ${bulkAction === "delete" ? "brutal-btn text-rose-300 border-rose-700" : "brutal-btn-primary"}`}
                            >
                                {bulkBusy ? "Working…" : "Confirm"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="brutal-card p-8 flex items-center justify-center gap-2 text-zinc-400" data-testid="tenants-loading">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading tenants…
                </div>
            ) : visible.length === 0 ? (
                <div className="brutal-card p-8 text-center text-zinc-400 text-sm" data-testid="tenants-empty">
                    {tenants.length === 0 ? "No tenants yet. Create the first one." : "No tenants match the search/filter."}
                </div>
            ) : (
                <div className="brutal-card overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-[#0a0a0a] border-b-2 border-[#333]">
                            <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500">
                                <th className="px-3 py-2 font-bold w-8">
                                    <input
                                        type="checkbox"
                                        checked={allSelected}
                                        onChange={toggleSelectAll}
                                        data-testid="tenants-select-all"
                                        className="accent-cyan-400"
                                    />
                                </th>
                                <th className="px-3 py-2 font-bold">Tenant</th>
                                <th className="px-3 py-2 font-bold">Slug</th>
                                <th className="px-3 py-2 font-bold">Custom domain</th>
                                <th className="px-3 py-2 font-bold">Mobile</th>
                                <th className="px-3 py-2 font-bold">Lifecycle</th>
                                <th className="px-3 py-2 font-bold text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visible.map((t) => (
                                <TenantRow
                                    key={t.id}
                                    tenant={t}
                                    isSelected={selected.has(t.id)}
                                    onToggleSelect={() => toggleSelect(t.id)}
                                    isEditing={editingId === t.id}
                                    onEdit={() => setEditingId(t.id)}
                                    onCancelEdit={() => setEditingId(null)}
                                    onToggleMobile={() => toggleMobile(t)}
                                    onSaved={(updated) => {
                                        setTenants((prev) => prev.map((x) => (x.id === t.id ? updated : x)));
                                        setEditingId(null);
                                    }}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {createOpen && (
                <TenantProvisioningWizard
                    onClose={() => setCreateOpen(false)}
                    onCreated={(t) => {
                        // Wizard fires this synchronously after step 1 — keep
                        // the modal open so the operator can finish step 2/3.
                        setTenants((prev) => {
                            if (prev.some((x) => x.id === t.id)) return prev;
                            return [...prev, t].sort((a, b) => {
                                if (a.is_default) return -1;
                                if (b.is_default) return 1;
                                return (a.name || "").localeCompare(b.name || "");
                            });
                        });
                    }}
                />
            )}
        </div>
    );
}

function TenantRow({ tenant, isSelected, onToggleSelect, isEditing, onEdit, onCancelEdit, onToggleMobile, onSaved }) {
    const [draft, setDraft] = useState({
        name: tenant.name,
        custom_domain: tenant.custom_domain || "",
        lifecycle_state: tenant.lifecycle_state,
    });
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            const body = {};
            if (draft.name !== tenant.name) body.name = draft.name;
            if ((draft.custom_domain || "") !== (tenant.custom_domain || "")) {
                body.custom_domain = draft.custom_domain || "";
            }
            if (draft.lifecycle_state !== tenant.lifecycle_state) {
                body.lifecycle_state = draft.lifecycle_state;
            }
            if (Object.keys(body).length === 0) {
                onCancelEdit();
                setSaving(false);
                return;
            }
            const r = await api.updateTenant(tenant.id, body);
            toast.success("Tenant updated");
            onSaved(r.tenant);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to update tenant");
        }
        setSaving(false);
    };

    const lifecycleLocked = tenant.is_default;

    return (
        <>
            <tr
                data-testid={`tenant-row-${tenant.id}`}
                className={`border-b border-[#222] ${tenant.lifecycle_state !== "active" ? "opacity-60" : ""}`}
            >
                <td className="px-3 py-3">
                    {!tenant.is_default && (
                        <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={onToggleSelect}
                            data-testid={`tenant-select-${tenant.id}`}
                            className="accent-cyan-400"
                        />
                    )}
                </td>
                <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                        {tenant.is_default && (
                            <span title="Default platform tenant" data-testid={`tenant-default-badge-${tenant.id}`}>
                                <Shield className="w-3.5 h-3.5 text-cyber" />
                            </span>
                        )}
                        <div>
                            <div className="font-bold text-white" data-testid={`tenant-name-${tenant.id}`}>{tenant.name}</div>
                            <div className="text-[10px] text-zinc-500 font-mono">{tenant.id.slice(0, 8)}…</div>
                        </div>
                    </div>
                </td>
                <td className="px-3 py-3 font-mono text-xs text-zinc-300" data-testid={`tenant-slug-${tenant.id}`}>{tenant.slug}</td>
                <td className="px-3 py-3 font-mono text-xs text-zinc-300" data-testid={`tenant-domain-${tenant.id}`}>
                    {tenant.custom_domain || <span className="text-zinc-600 italic font-sans">— none</span>}
                </td>
                <td className="px-3 py-3">
                    <button
                        type="button"
                        onClick={onToggleMobile}
                        data-testid={`tenant-mobile-toggle-${tenant.id}`}
                        className={`pill text-[10px] font-bold uppercase ${
                            tenant.mobile_enabled
                                ? "bg-cyber/20 text-cyber border-cyber"
                                : "bg-transparent text-zinc-500 border-[#333]"
                        }`}
                    >
                        <Smartphone className="w-3 h-3 inline mr-1" />
                        {tenant.mobile_enabled ? "On" : "Off"}
                    </button>
                </td>
                <td className="px-3 py-3">
                    <LifecycleBadge state={tenant.lifecycle_state} />
                </td>
                <td className="px-3 py-3 text-right">
                    {isEditing ? (
                        <button
                            type="button"
                            onClick={onCancelEdit}
                            data-testid={`tenant-cancel-edit-${tenant.id}`}
                            className="brutal-btn-ghost text-xs py-1.5"
                        >
                            <X className="w-3 h-3" /> Cancel
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={onEdit}
                            data-testid={`tenant-edit-${tenant.id}`}
                            className="brutal-btn-ghost text-xs py-1.5"
                        >
                            <Edit2 className="w-3 h-3" /> Edit
                        </button>
                    )}
                </td>
            </tr>
            {isEditing && (
                <tr className="bg-[#0a0a0a]" data-testid={`tenant-editor-${tenant.id}`}>
                    <td colSpan={7} className="px-3 py-4">
                        <div className="grid sm:grid-cols-3 gap-3">
                            <label className="text-xs space-y-1">
                                <span className="block text-zinc-500 uppercase tracking-wider text-[10px] font-bold">Name</span>
                                <input
                                    data-testid={`tenant-edit-name-${tenant.id}`}
                                    value={draft.name}
                                    onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                                    className="brutal-input w-full text-sm"
                                />
                            </label>
                            <label className="text-xs space-y-1">
                                <span className="block text-zinc-500 uppercase tracking-wider text-[10px] font-bold">Custom domain</span>
                                <input
                                    data-testid={`tenant-edit-domain-${tenant.id}`}
                                    value={draft.custom_domain}
                                    onChange={(e) => setDraft({ ...draft, custom_domain: e.target.value })}
                                    placeholder="crew.example.com"
                                    className="brutal-input w-full text-sm font-mono"
                                />
                            </label>
                            <label className="text-xs space-y-1">
                                <span className="block text-zinc-500 uppercase tracking-wider text-[10px] font-bold">
                                    Lifecycle
                                    {lifecycleLocked && (
                                        <span title="Default tenant can't be suspended/archived" className="ml-1 text-amber-400">
                                            <AlertTriangle className="w-3 h-3 inline" />
                                        </span>
                                    )}
                                </span>
                                <select
                                    data-testid={`tenant-edit-lifecycle-${tenant.id}`}
                                    value={draft.lifecycle_state}
                                    onChange={(e) => setDraft({ ...draft, lifecycle_state: e.target.value })}
                                    disabled={lifecycleLocked}
                                    className="brutal-input w-full text-sm disabled:opacity-50"
                                >
                                    {LIFECYCLE_OPTIONS.map((o) => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                </select>
                            </label>
                        </div>
                        <div className="mt-3 flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={onCancelEdit}
                                className="brutal-btn-ghost text-xs py-1.5"
                                data-testid={`tenant-editor-cancel-${tenant.id}`}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={save}
                                disabled={saving}
                                data-testid={`tenant-editor-save-${tenant.id}`}
                                className="brutal-btn-primary text-xs py-1.5 disabled:opacity-60"
                            >
                                <CheckCircle2 className="w-3 h-3" /> {saving ? "Saving…" : "Save"}
                            </button>
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
}
